<script lang="ts">
  import Breadcrumb from '../shared/Breadcrumb.svelte';
  import EnvSwitcher from '../shared/EnvSwitcher.svelte';
  import DataTable from '../shared/DataTable.svelte';
  import JsonViewer from '../shared/JsonViewer.svelte';
  import type { Column } from '../shared/DataTable.svelte';
  import { queryDdb, getKeyFields, type DdbTable, type DdbRecord } from '../../services/ddb-client';

  let env = $state<'Beta' | 'Prod'>('Beta');
  let selectedTable = $state<DdbTable>('CS3');
  let keyValues = $state<Record<string, string>>({});
  let results = $state<DdbRecord[]>([]);
  let loading = $state(false);
  let queried = $state(false);
  let expandedRow = $state<number | null>(null);

  const tables: DdbTable[] = ['CS3', 'OutputCache', 'UnadASIN'];
  const keyFields = $derived(getKeyFields(selectedTable));

  function resetForm() {
    keyValues = {};
    results = [];
    queried = false;
    expandedRow = null;
  }

  async function runQuery() {
    loading = true;
    queried = true;
    expandedRow = null;
    try {
      results = await queryDdb(env, { table: selectedTable, ...keyValues });
    } finally {
      loading = false;
    }
  }

  const columns = $derived.by<Column[]>(() => {
    if (results.length === 0) return [];
    const keys = Object.keys(results[0]).filter((k) => typeof results[0][k] !== 'object');
    return keys.map((k) => ({ key: k, label: k.replace(/_/g, ' ') }));
  });

  const hasObjectFields = $derived(
    results.length > 0 && Object.values(results[0]).some((v) => typeof v === 'object' && v !== null),
  );
</script>

<div class="min-h-screen bg-bg-dark text-text-primary">
  <div class="w-full px-6 py-4">
    <div class="flex items-center justify-between pr-20">
      <Breadcrumb items={[{ label: 'DynamoDB Viewer' }]} />
      <EnvSwitcher bind:value={env} />
    </div>

    <div class="mt-4 mb-6">
      <h1 class="text-2xl font-bold">🗄️ DynamoDB Viewer</h1>
      <p class="mt-1 text-sm text-text-secondary">Query CS3 strategies, output cache, and UnadASIN tables</p>
    </div>

    <!-- Query Form -->
    <div class="bg-bg-panel border border-border rounded-xl p-5 mb-6">
      <div class="flex flex-wrap items-end gap-4">
        <div>
          <label for="table-select" class="block text-xs text-text-secondary mb-1.5">Table</label>
          <select
            id="table-select"
            bind:value={selectedTable}
            onchange={() => { results = []; queried = false; expandedRow = null; }}
            class="bg-bg-input border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent"
          >
            {#each tables as t}
              <option value={t}>{t}</option>
            {/each}
          </select>
        </div>

        {#each keyFields as field (field.key)}
          <div class="flex-1 min-w-[180px]">
            <label for={field.key} class="block text-xs text-text-secondary mb-1.5">
              {field.label}
              {#if field.required}<span class="text-error">*</span>{/if}
            </label>
            <input
              id={field.key}
              type="text"
              bind:value={keyValues[field.key]}
              placeholder={field.key === 'advertiser_id' ? 'A1B2C3D4E5' : ''}
              class="w-full bg-bg-input border border-border rounded-lg px-3 py-2 text-sm text-text-primary font-mono placeholder:text-text-muted focus:outline-none focus:border-accent"
            />
          </div>
        {/each}

        <button
          onclick={runQuery}
          disabled={loading}
          class="px-5 py-2 bg-accent hover:bg-accent-hover text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {loading ? 'Querying…' : 'Query'}
        </button>
      </div>
    </div>

    <!-- Results -->
    {#if loading}
      <DataTable columns={[{ key: 'a', label: '' }]} rows={[]} loading={true} />
    {:else if queried && results.length === 0}
      <div class="bg-bg-panel border border-border rounded-xl p-12 text-center">
        <p class="text-4xl mb-3">🔍</p>
        <p class="text-text-secondary">
          No records found for advertiser <span class="font-mono text-text-primary">{keyValues['advertiser_id'] || '—'}</span> in <span class="font-semibold">{selectedTable}</span> ({env})
        </p>
        <p class="text-xs text-text-muted mt-2">totalItems: 0</p>
        <p class="text-xs text-text-muted mt-2">
          Tip: Try switching to {env === 'Beta' ? 'Prod' : 'Beta'} — data may only exist in one environment.
        </p>
      </div>
    {:else if results.length > 0}
      <div class="mb-3 text-xs text-text-secondary">{results.length} record{results.length !== 1 ? 's' : ''} returned</div>
      <DataTable {columns} rows={results} />

      {#if hasObjectFields}
        <div class="mt-6">
          <h3 class="text-sm font-medium text-text-secondary mb-3">Expand Record Details</h3>
          <div class="space-y-2">
            {#each results as row, i (i)}
              <div class="bg-bg-panel border border-border rounded-lg overflow-hidden">
                <button
                  onclick={() => expandedRow = expandedRow === i ? null : i}
                  class="w-full flex items-center justify-between px-4 py-3 text-sm text-left hover:bg-bg-hover transition-colors"
                >
                  <span class="font-mono text-xs text-text-primary">
                    {row.strategy_id || row.cache_key || row.asin || `Record ${i + 1}`}
                  </span>
                  <span class="text-text-muted transition-transform {expandedRow === i ? 'rotate-180' : ''}">▼</span>
                </button>
                {#if expandedRow === i}
                  <div class="px-4 pb-4 border-t border-border-light pt-3 animate-fade-in-up">
                    <JsonViewer data={row} maxDepth={4} />
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        </div>
      {/if}
    {/if}
  </div>
</div>
